// Session/auth state for the operator console. Wraps the real core-api auth endpoints:
// on mount it resolves the current operator (GET /api/auth/me) and, when signed in, loads the
// effective config (ruleset + fail-closed flags). login/logout use the cookie session.
//
// Owner tenant-context (#232): the cross-tenant owner browses a business by naming it per
// request (X-Tenant-Id — set on the module-level client). The selection lives here, persists
// in localStorage (owner sessions only) and re-resolves me()+config so `user.tenantId/
// tenantStatus` always reflect the browsed tenant. Non-owner sessions always clear it.
import React from "react";
import { cockpit, getTenantContext, setTenantContext } from "@/api";
import { clearReadCache } from "@/api/read-cache";
import type { AppConfigInfo, SessionUser } from "@/api";

const TENANT_CONTEXT_KEY = "neo_tenant_context";

function readStoredTenantContext(): string | null {
  try {
    return window.localStorage.getItem(TENANT_CONTEXT_KEY);
  } catch {
    return null;
  }
}

function persistTenantContext(tenantId: string | null): void {
  try {
    if (tenantId) window.localStorage.setItem(TENANT_CONTEXT_KEY, tenantId);
    else window.localStorage.removeItem(TENANT_CONTEXT_KEY);
  } catch {
    /* ignore */
  }
}

interface SessionContextValue {
  user: SessionUser | null;
  ready: boolean; // the initial me() probe has resolved
  config: AppConfigInfo | null;
  /** Owner-only: the tenant currently browsed via explicit context (null = owner plane). */
  tenantContext: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Reload the authenticated identity after a self-service profile update. */
  refreshUser: () => Promise<SessionUser | null>;
  /** `background` keeps the previous config when the refresh fails (live events). */
  refreshConfig: (options?: { background?: boolean }) => Promise<void>;
  /** Owner-only: enter a business from the Businesses screen (#1283). */
  switchTenant: (tenantId: string) => Promise<void>;
}

const SessionContext = React.createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<SessionUser | null>(null);
  const [ready, setReady] = React.useState(false);
  const [config, setConfig] = React.useState<AppConfigInfo | null>(null);
  const [tenantContext, setTenantContextState] = React.useState<string | null>(null);

  const refreshConfig = React.useCallback(async (options?: { background?: boolean }) => {
    // The browsed business can change while this is in flight; a response for the previous one
    // must never become the config the console renders (#544).
    const fetchedFor = getTenantContext();
    try {
      const fresh = await cockpit.getConfig();
      if (getTenantContext() !== fetchedFor) return;
      setConfig(fresh);
    } catch (error) {
      // A failed EXPLICIT refresh means the console can no longer prove that its cached
      // business-policy identity is current. Drop it before propagating the error so every
      // authoring surface fails closed instead of remaining green on stale provenance. A
      // background refresh (a live event) keeps the previous config: a transient failure must not
      // silently blank the flags the shell renders.
      if (!options?.background) setConfig(null);
      throw error;
    }
  }, []);

  const loadConfig = React.useCallback(async () => {
    try {
      await refreshConfig();
    } catch {
      setConfig(null);
    }
  }, [refreshConfig]);

  /** Adopt a freshly resolved session: owners get their persisted tenant context re-applied
   *  (dropped fail-closed if it no longer resolves); everyone else always runs context-free. */
  const adoptUser = React.useCallback(
    async (fresh: SessionUser | null): Promise<SessionUser | null> => {
      if (!fresh || fresh.role !== "owner") {
        setTenantContext(null);
        setTenantContextState(null);
        return fresh;
      }
      const stored = readStoredTenantContext();
      if (!stored) {
        setTenantContext(null);
        setTenantContextState(null);
        return fresh;
      }
      setTenantContext(stored);
      try {
        const scoped = await cockpit.me();
        if (scoped) {
          setTenantContextState(stored);
          return scoped;
        }
      } catch {
        /* stale/deleted tenant → fall through and drop the context */
      }
      setTenantContext(null);
      persistTenantContext(null);
      setTenantContextState(null);
      return fresh;
    },
    [],
  );

  // Initial probe: are we already signed in? #2302: an owner's stored business goes with the first
  // probe, so a reload resolves the session in one round trip; the live stream and the pages behind
  // it wait for this. A probe the stored business fails (another role, an unknown business, signed
  // out) is repeated without it and adopted as before, which drops that business fail-closed.
  React.useEffect(() => {
    let alive = true;
    const probe = async (): Promise<SessionUser | null> => {
      const stored = readStoredTenantContext();
      if (stored) {
        setTenantContext(stored);
        const scoped = await cockpit.me().catch(() => null);
        if (scoped?.role === "owner") {
          setTenantContextState(stored);
          return scoped;
        }
        setTenantContext(null);
      }
      return adoptUser(await cockpit.me());
    };
    probe()
      .then((adopted) => {
        if (!alive) return;
        setUser(adopted);
        if (adopted) void loadConfig();
      })
      .catch(() => alive && setUser(null))
      .finally(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, [adoptUser, loadConfig]);

  const login = React.useCallback(
    async (email: string, password: string) => {
      const u = await cockpit.login(email, password);
      const adopted = await adoptUser(u);
      setUser(adopted);
      await loadConfig();
    },
    [adoptUser, loadConfig],
  );

  const logout = React.useCallback(async () => {
    try {
      await cockpit.logout();
    } finally {
      // The persisted owner selection survives logout; the in-memory context must not leak
      // into whichever session signs in next.
      setTenantContext(null);
      setTenantContextState(null);
      clearReadCache();
      setUser(null);
      setConfig(null);
    }
  }, []);

  const refreshUser = React.useCallback(async () => {
    const fetchedFor = getTenantContext();
    const fresh = await cockpit.me();
    // A switch while this was in flight already installed the new identity; writing this one back
    // would leave `user.tenantId` pointing at the previous business while every request carries
    // the new one — the live stream would then reject its own events.
    if (getTenantContext() !== fetchedFor) return fresh;
    // #1536: an ended session must not leave its answers for whoever signs in next.
    if (!fresh) clearReadCache();
    // Keep the previous object when nothing changed: a live refresh (#544) runs on every settings
    // or business change, and a new identity object would re-run every effect that depends on it.
    setUser((previous) =>
      previous && fresh && JSON.stringify(previous) === JSON.stringify(fresh) ? previous : fresh,
    );
    return fresh;
  }, []);

  const switchTenant = React.useCallback(
    async (tenantId: string) => {
      // Effective features belong to the current tenant context. Blank them synchronously before
      // changing that context so no render can pair the next tenant with the previous tenant's
      // config while me() and config are being re-resolved.
      setConfig(null);
      setUser((previous) => previous ? { ...previous, tenantDisplayName: null } : previous);
      setTenantContext(tenantId);
      // #1536: answers remembered for the previous business are never shown for this one.
      clearReadCache();
      try {
        const scoped = await cockpit.me();
        // A newer switch owns the session now; this response must not replace its identity.
        if (getTenantContext() !== tenantId) return;
        if (scoped) {
          persistTenantContext(tenantId);
          setTenantContextState(tenantId);
          setUser(scoped);
          await loadConfig();
          return;
        }
      } catch {
        /* unknown tenant / revoked session → fail closed below */
      }
      if (getTenantContext() !== tenantId) return;
      // Fail-closed: drop the context entirely rather than keep a broken one.
      setTenantContext(null);
      clearReadCache();
      persistTenantContext(null);
      setTenantContextState(null);
      const u = await cockpit.me().catch(() => null);
      if (getTenantContext() !== null) return;
      setUser(u);
      if (u) await loadConfig();
    },
    [loadConfig],
  );

  const value: SessionContextValue = {
    user,
    ready,
    config,
    tenantContext,
    login,
    logout,
    refreshUser,
    refreshConfig,
    switchTenant,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within <SessionProvider>");
  return ctx;
}
